import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api/axios";
import { toast } from "react-toastify";
import { FiClock, FiUser, FiHome } from "react-icons/fi";
import "./VisitCalendar.scss";

/* ───────── Yardımcılar ───────── */
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toMonthInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);

const VIEW = { DAY: "day", MONTH: "month", YEAR: "year" };

/* 07:00 → ertesi gün 02:00 aralığı (19 saat) */
const GRID_HOURS = 19;
const HOUR_PX = 60;      // 1 saat = 60px
const MIN_EVENT_PX = 72; // event minimum yüksekliği

const COLORS = ["#60a5fa", "#34d399", "#fbbf24", "#f87171", "#a78bfa", "#22d3ee", "#f472b6", "#f97316", "#84cc16", "#e879f9", "#38bdf8"];
const hashColorFromId = (id) => COLORS[(String(id ?? "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % COLORS.length];

const STATUS_LABEL = {
  PENDING: "Henüz yapılmadı",
  PLANNED: "Planlandı",
  COMPLETED: "Yapıldı",
  FAILED: "Yapılamadı",
  CANCELLED: "İptal edildi",
  POSTPONED: "Ertelendi",
};
const statusLabel = (s) => STATUS_LABEL[s] || "—";
const statusClass = (s) => `st-${(s || "PLANNED").toLowerCase()}`;

const VISIT_TYPE_TR = {
  PERIYODIK: "Periyodik Ziyaret",
  ACIL_CAGRI: "Acil Çağrı",
  ISTASYON_KURULUM: "İstasyon Kurulum",
  ILK_ZIYARET: "İlk Ziyaret",
};

/* ───────── Responsive gün sütun sayısı (3 → 2 → 1) ───────── */
function useResponsiveDayCols() {
  const [cols, setCols] = useState(3);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || 1280;
      setCols(w >= 1280 ? 3 : w >= 900 ? 2 : 1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  return cols;
}

/* ───────── Çakışan etkinlikleri lane’lere dağıt ───────── */
function assignLanesForDay(list) {
  const evs = [...list].sort((a, b) => a.start - b.start);
  const laneEnds = [];
  for (const ev of evs) {
    let placed = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (ev.start >= laneEnds[i]) {
        ev._lane = i;
        laneEnds[i] = ev.end;
        placed = true;
        break;
      }
    }
    if (!placed) {
      ev._lane = laneEnds.length;
      laneEnds.push(ev.end);
    }
  }
  return { events: evs, laneCount: Math.max(1, laneEnds.length) };
}

/* ───────── Personel & Müşteri & Mağaza fetch ───────── */
async function fetchEmployees() {
  try {
    const { data } = await api.get("/employees");
    return Array.isArray(data) ? data : [];
  } catch {
    try {
      const { data } = await api.get("/admin/employees");
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }
}
async function fetchCustomers() {
  const tries = ["/customers", "/admin/customers"];
  for (const t of tries) {
    try {
      const { data } = await api.get(t);
      if (Array.isArray(data)) return data;
    } catch { }
  }
  return [];
}
async function fetchStoresByCustomer(customerId) {
  if (!customerId) return [];
  try {
    const { data } = await api.get(`/stores/customer/${customerId}`);
    return Array.isArray(data) ? data : [];
  } catch { return []; }
}
function rankStores(list, q) {
  if (!q) return list;
  const needle = q.toLowerCase();
  const score = (s) => {
    const t = (s || "").toLowerCase();
    if (t.startsWith(needle)) return 0;
    if (t.includes(needle)) return 1;
    return 2;
  };
  return [...list].sort((a, b) => {
    const as = Math.min(score(a.name), score(a.code));
    const bs = Math.min(score(b.name), score(b.code));
    if (as !== bs) return as - bs;
    return (a.name || "").localeCompare(b.name || "");
  });
}

/* ───────── Events API ───────── */
async function fetchEventsInRange(from, to) {
  const qs = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  try {
    const { data } = await api.get(`/schedule/events?${qs.toString()}`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("GET /schedule/events hata:", err);
    toast.error(err?.response?.data?.error || "Etkinlikler getirilemedi");
    return [];
  }
}
async function createEvent(payload) {
  const { title, notes, employeeId, storeId, start, end } = payload;
  const body = { title, notes, employeeId, storeId, start, end };
  const { data } = await api.post("/schedule/events", body);
  return data;
}
async function deleteEvent(id) {
  const { data } = await api.delete(`/schedule/events/${id}`);
  return data;
}

/* ───────── Gün penceresi 07:00 → 02:00 ───────── */
function dayWindow(d) {
  const ws = new Date(d); ws.setHours(7, 0, 0, 0);
  const we = new Date(d); we.setDate(we.getDate() + 1); we.setHours(2, 0, 0, 0);
  return { ws, we };
}

export default function VisitCalendar() {
  const navigate = useNavigate();

  /* Profil → rol ve kendi id'si (employee ise) */
  const [me, setMe] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setMe(data || null);
      } catch {
        setMe(null);
      }
    })();
  }, []);
  const localRole = (localStorage.getItem("role") || "").toLowerCase();
  const role = String(me?.role || localRole || "").toLowerCase();
  const selfId = Number(me?.id ?? me?.userId ?? me?.employeeId ?? 0) || null;

  const isAdmin = role === "admin";
  const isEmployee = role === "employee";
  const isLevel1 = isEmployee && me?.level === 1; // Changed from isLevel2
  const isPlanner = isAdmin || isLevel1; // Changed to use isLevel1

  const dayCols = useResponsiveDayCols();

  const [view, setView] = useState(VIEW.DAY);
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalDefaults, setModalDefaults] = useState(null);

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, title: "" });
  const [deleting, setDeleting] = useState(false);

  // Create form state
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [storesAll, setStoresAll] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeQ, setStoreQ] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [storesLoading, setStoresLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Recurrance state
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState("WEEKLY");
  const [duration, setDuration] = useState("1Y");

  // Range
  const range = useMemo(() => {
    if (view === VIEW.DAY) {
      const d0 = new Date(anchor); d0.setDate(d0.getDate() - 1);
      const d1 = new Date(anchor); d1.setDate(d1.getDate() + 2);
      return { from: startOfDay(d0), to: endOfDay(d1) };
    }
    if (view === VIEW.MONTH) return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
    return { from: startOfYear(anchor), to: endOfYear(anchor) };
  }, [anchor, view]);

  // Load events
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await fetchEventsInRange(range.from, range.to);
      const norm = raw.map((e) => ({
        ...e,
        start: new Date(e.start),
        end: new Date(e.end),
        color: e.color || hashColorFromId(e.employeeId ?? e.storeId),
        status: e.status || "PLANNED",
      }));
      setEvents(norm);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Takvim yüklenemedi");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  // Employees / Customers
  useEffect(() => { (async () => setEmployees(await fetchEmployees()))(); }, []);
  useEffect(() => { (async () => setCustomers(await fetchCustomers()))(); }, []);

  // Employee list’e kendini ekle (liste dönmezse select kilitlenmesin)
  useEffect(() => {
    if (isEmployee && selfId && !employees.find(e => e.id === selfId)) {
      setEmployees(prev => [{ id: selfId, fullName: me?.fullName || me?.name || me?.email || `Personel #${selfId}` }, ...prev]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployee, selfId, me]);

  // Customer → Stores
  useEffect(() => {
    (async () => {
      setStoresAll([]); setStores([]); setSelectedStoreId(null);
      if (!selectedCustomerId) return;
      setStoresLoading(true);
      const list = await fetchStoresByCustomer(selectedCustomerId);
      setStoresAll(Array.isArray(list) ? list : []);
      setStoresLoading(false);
    })();
  }, [selectedCustomerId]);

  // Store search
  useEffect(() => {
    const q = storeQ.trim();
    const ranked = rankStores(storesAll, q).slice(0, 12);
    setStores(ranked);
  }, [storeQ, storesAll]);

  // Day hour labels (07..23, 00..01)
  const dayHourLabels = useMemo(() => {
    if (view !== VIEW.DAY) return [];
    const labels = [];
    for (let h = 7; h <= 23; h++) labels.push(`${pad2(h)}:00`);
    labels.push("00:00", "01:00");
    return labels;
  }, [view]);

  // Day columns by responsive count
  const dayColumns = useMemo(() => {
    if (view !== VIEW.DAY) return [];
    const offsets =
      dayCols === 3 ? [-1, 0, 1] :
        dayCols === 2 ? [0, 1] :
          [0];
    return offsets.map((off) => {
      const d = new Date(anchor);
      d.setDate(d.getDate() + off);
      return d;
    });
  }, [view, anchor, dayCols]);

  // Events per displayed day (clipped to window 07→02)
  const dayEventsByDate = useMemo(() => {
    if (view !== VIEW.DAY) return {};
    const map = {};
    for (const d of dayColumns) {
      const key = toDateInput(d);
      const { ws, we } = dayWindow(d);
      const winEvents = [];
      for (const e of events) {
        if (e.end <= ws || e.start >= we) continue;
        const s = new Date(Math.max(e.start, ws));
        const ee = new Date(Math.min(e.end, we));
        winEvents.push({
          ...e,
          start: s,
          end: ee,
          _relStartMin: Math.round((s - ws) / 60000),
          _relEndMin: Math.round((ee - ws) / 60000),
        });
      }
      map[key] = assignLanesForDay(winEvents);
    }
    return map;
  }, [view, dayColumns, events]);

  // Month view
  const monthLabel = useMemo(
    () => anchor.toLocaleDateString("tr-TR", { month: "long", year: "numeric" }),
    [anchor]
  );
  const monthMatrix = useMemo(() => {
    if (view !== VIEW.MONTH) return [];
    const first = startOfMonth(anchor);
    const last = endOfMonth(anchor);
    const start = new Date(first);
    start.setDate(first.getDate() - (first.getDay() || 7) + 1);
    const weeks = [];
    let cur = new Date(start);
    while (cur <= last || weeks.length < 6) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        row.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
      }
      weeks.push(row);
      if (cur > last && weeks.length >= 6) break;
    }
    return weeks;
  }, [view, anchor]);
  const monthEventsByDayKey = useMemo(() => {
    if (view !== VIEW.MONTH) return {};
    const map = {};
    for (const ev of events) {
      const key = toDateInput(ev.start);
      (map[key] ||= []).push(ev);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.start - b.start);
    return map;
  }, [view, events]);

  // Year view
  const yearMonths = useMemo(() => {
    if (view !== VIEW.YEAR) return [];
    return Array.from({ length: 12 }, (_, i) => new Date(anchor.getFullYear(), i, 1));
  }, [view, anchor]);

  // UI helpers
  const goToday = () => setAnchor(new Date());
  const prev = () => {
    if (view === VIEW.DAY) { const d = new Date(anchor); d.setDate(d.getDate() - 1); setAnchor(d); }
    else if (view === VIEW.MONTH) { const d = new Date(anchor); d.setMonth(d.getMonth() - 1); setAnchor(d); }
    else { const d = new Date(anchor); d.setFullYear(d.getFullYear() - 1); setAnchor(d); }
  };
  const next = () => {
    if (view === VIEW.DAY) { const d = new Date(anchor); d.setDate(d.getDate() + 1); setAnchor(d); }
    else if (view === VIEW.MONTH) { const d = new Date(anchor); d.setMonth(d.getMonth() + 1); setAnchor(d); }
    else { const d = new Date(anchor); d.setFullYear(d.getFullYear() + 1); setAnchor(d); }
  };

  const openCreateForDay = (day, defaults) => {
    if (!isPlanner) return;
    setModalDate(startOfDay(day));
    setModalDefaults(defaults || null);
    setModalOpen(true);
  };

  /* Silme onay modalını aç */
  const openDeleteConfirm = (evId, evTitle) => {
    setDeleteConfirm({ open: true, id: evId, title: evTitle || "Ziyaret" });
  };
  const closeDeleteConfirm = () => {
    setDeleteConfirm({ open: false, id: null, title: "" });
  };
  const handleConfirmDelete = async () => {
    if (!deleteConfirm.id) return;
    setDeleting(true);
    try {
      await deleteEvent(deleteConfirm.id);
      toast.success("Ziyaret silindi.");
      closeDeleteConfirm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Silinemedi.");
    } finally {
      setDeleting(false);
    }
  };

  /* Ay görünümünde bir güne tıklayınca Gün görünümüne geç */
  const goToDay = (d) => {
    setAnchor(new Date(d));
    setView(VIEW.DAY);
  };

  const formRef = useRef(null);
  const onCreate = async (e) => {
    e.preventDefault();
    if (!isPlanner) return;
    const form = formRef.current;
    if (!form) return toast.error("Form bulunamadı.");

    const fd = new FormData(form);
    // Employee rolünde → her zaman KENDİ id’si
    const employeeId = isEmployee ? (selfId || 0) : Number(fd.get("employeeId") || 0);
    const pickedStoreId = Number(fd.get("storeId") || selectedStoreId || 0);

    const visitTypeKey = fd.get("visitType") || "";
    const title = VISIT_TYPE_TR[visitTypeKey] || "Ziyaret";
    const notes = fd.get("notes") || "";

    if (!employeeId) return toast.error("Personel seçin");
    if (!pickedStoreId) return toast.error("Mağaza seçin");

    const startTime = fd.get("start");
    const endTime = fd.get("end");
    if (!startTime || !endTime) return toast.error("Başlangıç ve bitiş saati seçin");

    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);

    const start = new Date(modalDate);
    if (sh < 7) start.setDate(start.getDate() + 1);
    start.setHours(sh, sm, 0, 0);

    const end = new Date(modalDate);
    if (eh < 7) end.setDate(end.getDate() + 1);
    end.setHours(eh, em, 0, 0);

    if (end <= start) return toast.error("Bitiş, başlangıçtan sonra olmalı");

    try {
      setSaving(true);
      if (isRecurring) {
        const res = await api.post("/schedule/events/bulk", {
          title, 
          notes, 
          employeeId, 
          storeId: pickedStoreId, 
          start, 
          end,
          frequency,
          duration
        });
        toast.success(res.data.message || "Tekrarlanan ziyaretler planlandı");
      } else {
        await createEvent({ title, notes, employeeId, storeId: pickedStoreId, start, end });
        toast.success("Ziyaret planlandı");
      }
      setModalOpen(false);
      setSelectedStoreId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const isSelectedDate = (d) => d.toDateString() === anchor.toDateString();

  return (
    <Layout title="Ziyaret Takvimi">
      <div className="vc-page">
        {/* Toolbar */}
        <div className="vc-toolbar">
          <div className="left">
            <div className="seg">
              <button className={view === VIEW.DAY ? "on" : ""} onClick={() => setView(VIEW.DAY)}>Gün</button>
              <button className={view === VIEW.MONTH ? "on" : ""} onClick={() => setView(VIEW.MONTH)}>Ay</button>
              <button className={view === VIEW.YEAR ? "on" : ""} onClick={() => setView(VIEW.YEAR)}>Yıl</button>
            </div>
            <div className="seg">
              <button onClick={prev}>‹</button>
              <button onClick={goToday}>Bugün</button>
              <button onClick={next}>›</button>
            </div>
          </div>

          <div className="right">
            {view === VIEW.DAY && (
              <input
                type="date"
                value={toDateInput(anchor)}
                onChange={(e) => {
                  const [y, m, d] = e.target.value.split("-").map(Number);
                  setAnchor(new Date(y, m - 1, d));
                }}
              />
            )}
            {view === VIEW.MONTH && (
              <input
                type="month"
                value={toMonthInput(anchor)}
                onChange={(e) => {
                  const [y, m] = e.target.value.split("-").map(Number);
                  setAnchor(new Date(y, m - 1, 1));
                }}
              />
            )}
            {view === VIEW.YEAR && (
              <input
                type="number"
                value={anchor.getFullYear()}
                onChange={(e) => {
                  const y = Number(e.target.value || new Date().getFullYear());
                  setAnchor(new Date(y, 0, 1));
                }}
              />
            )}
          </div>
        </div>

        {/* Legend */}
        {employees?.length > 0 && (
          <div className="vc-legend card">
            {employees.map(e => (
              <div className="lg-item" key={e.id}>
                <span className="dot" style={{ background: hashColorFromId(e.id) }} />
                <span className="name">{e.fullName || e.name || e.email}</span>
              </div>
            ))}
          </div>
        )}



        {/* BODY */}
        {loading ? (
          <div className="card">Yükleniyor…</div>
        ) : view === VIEW.DAY ? (
          <div
            className="vc-day"
            style={{ "--grid-hours": GRID_HOURS, "--day-cols": dayCols }}
          >
            {/* Saat kolonu */}
            <div className="hours">
              <div className="hours-head" />
              <div className="hours-grid">
                {dayHourLabels.map((lbl, i) => (
                  <div className="h" key={i}>{lbl}</div>
                ))}
              </div>
            </div>

            {/* Günler */}
            <div className="days">
              {dayColumns.map((d) => {
                const key = toDateInput(d);
                const meta = dayEventsByDate[key] || { events: [], laneCount: 1 };
                return (
                  <div className={`col ${isSelectedDate(d) ? "is-selected" : ""}`} key={key}>
                    <div className="col-head" onClick={() => openCreateForDay(d)}>
                      {toDateInput(d)}{isSelectedDate(d) ? " • Seçili" : ""}
                    </div>

                    <div
                      className="col-grid"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const totalGridHeight = rect.height; // e.g. 24 hours * HOUR_PX
                        const fractionalDay = y / totalGridHeight;
                        const rawMinutes = fractionalDay * GRID_HOURS * 60;
                        const roundedMinutes = Math.max(0, Math.floor(rawMinutes / 15) * 15);
                        // Grid 07:00'de başlar, o yüzden 7 saat (420 dk) eklemeliyiz
                        const finalMinutes = roundedMinutes + 420;
                        openCreateForDay(d, {
                          startMin: finalMinutes,
                          endMin: finalMinutes + 30
                        });
                      }}
                      style={{ "--lane-count": meta.laneCount }}
                    >
                      {meta.events.map((ev) => {
                        const topPx = (ev._relStartMin / 60) * HOUR_PX;
                        const durMin = Math.max(1, ev._relEndMin - ev._relStartMin);
                        const heightPx = Math.max((durMin / 60) * HOUR_PX, MIN_EVENT_PX);

                        return (
                          <div
                            key={ev.id}
                            className={`event ${statusClass(ev.status)}`}
                            style={{
                              "--lane-index": ev._lane,
                              "--ev-color": ev.color,
                              top: `${topPx}px`,
                              height: `${heightPx}px`,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/calendar/visit/${ev.id}`);
                            }}
                            title={`${ev.title} • ${ev.employeeName || ""} • ${ev.storeName || ""}`}
                          >
                            <div className="ev-header">
                              <span className={`badge ${statusClass(ev.status)}`}>{statusLabel(ev.status)}</span>
                              <div className="ev-time">
                                <FiClock />
                                {pad2(ev.start.getHours())}:{pad2(ev.start.getMinutes())}
                              </div>
                            </div>
                            
                            {isPlanner && (
                              <button
                                className="ev-delete-btn"
                                title="Sil"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteConfirm(ev.id, ev.title);
                                }}
                              >×</button>
                            )}

                            <div className="ev-title">{ev.title}</div>
                            
                            <div className="ev-details">
                              {ev.employeeName && (
                                <div className="ev-detail-item">
                                  <FiUser /> <span>{ev.employeeName}</span>
                                </div>
                              )}
                              {ev.storeName && (
                                <div className="ev-detail-item">
                                  <FiHome /> <span>{ev.storeName}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === VIEW.MONTH ? (
          <div className="vc-month">
            <div className="month-nav">
              <button className="nav-btn" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}>‹</button>
              <div className="month-title">{monthLabel}</div>
              <button className="nav-btn" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}>›</button>
            </div>

            <div className="dow">
              {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((l) => <div key={l}>{l}</div>)}
            </div>

            <div className="grid">
              {monthMatrix.map((row, ri) => (
                <div className="row" key={ri}>
                  {row.map((d) => {
                    const key = toDateInput(d);
                    const dayEvs = (monthEventsByDayKey[key] || []).sort((a, b) => a.start - b.start);
                    const isOtherMonth = d.getMonth() !== anchor.getMonth();
                    return (
                      <div
                        className={`cell ${isOtherMonth ? "muted" : ""}`}
                        key={key}
                        onClick={() => goToDay(d)}
                        title={`${toDateInput(d)} gününe git`}
                      >
                        <div
                          className="date"
                          onClick={(e) => { e.stopPropagation(); goToDay(d); }}
                        >
                          {d.getDate()}
                        </div>
                        <div className="list">
                          {dayEvs.slice(0, 3).map((ev) => (
                            <div
                              key={ev.id}
                              className="pill"
                              style={{ background: ev.color, cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/calendar/visit/${ev.id}`);
                              }}
                              title={`${ev.title} • ${ev.employeeName || ""} • ${ev.storeName || ""}`}
                            >
                              {isPlanner && (
                                <button
                                  className="pill-delete-btn"
                                  title="Sil"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteConfirm(ev.id, ev.title);
                                  }}
                                >×</button>
                              )}
                              <span className={`badge xs ${statusClass(ev.status)}`}>{statusLabel(ev.status)}</span>
                              <span className="t">{ev.title}</span>
                              <span className="m">
                                {pad2(ev.start.getHours())}:{pad2(ev.start.getMinutes())}
                              </span>
                            </div>
                          ))}
                          {dayEvs.length > 3 && <div className="more" onClick={(e) => { e.stopPropagation(); goToDay(d); }}>+{dayEvs.length - 3} daha</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="vc-year">
            {yearMonths.map((m) => {
              const label = `${m.getFullYear()} / ${pad2(m.getMonth() + 1)}`;
              const monthStart = startOfMonth(m);
              const monthEnd = endOfMonth(m);
              const count = events.filter(ev => ev.start >= monthStart && ev.start <= monthEnd).length;
              return (
                <div className="ym" key={label} onClick={() => { setView(VIEW.MONTH); setAnchor(m); }}>
                  <div className="ym-head">{label}</div>
                  <div className="ym-body">
                    <span className="count-badge">{count}</span> planlı ziyaret
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CREATE MODAL */}
        {modalOpen && (
          <div
            className="vc-modal-backdrop"
            onClick={(e) => {
              if (e.target.classList.contains("vc-modal-backdrop")) setModalOpen(false);
            }}
          >
            <div className="vc-modal">
              <div className="vc-modal-head">
                <div>Ziyaret Planla</div>
                <button className="close" onClick={() => setModalOpen(false)}>×</button>
              </div>

              <form
                className="vc-modal-body"
                onSubmit={onCreate}
                ref={formRef}
                key={modalDate?.toISOString() + (modalDefaults?.startMin || 0)}
              >
                <input type="hidden" name="storeId" value={selectedStoreId ?? ""} />

                <div className="field-row">
                  <div className="field">
                    <label>Tarih</label>
                    <input type="date" value={toDateInput(modalDate)} readOnly />
                  </div>

                  <div className="field">
                    <label>Başlangıç</label>
                    <select name="start" defaultValue={
                      modalDefaults?.startMin != null
                        ? `${pad2(Math.floor(modalDefaults.startMin / 60))}:${pad2(modalDefaults.startMin % 60)}`
                        : "09:00"
                    }>
                      {Array.from({ length: 96 }, (_, i) => {
                        const m = i * 15;
                        return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
                      }).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="field">
                    <label>Bitiş</label>
                    <select name="end" defaultValue={
                      modalDefaults?.endMin != null
                        ? `${pad2(Math.floor(modalDefaults.endMin / 60))}:${pad2(modalDefaults.endMin % 60)}`
                        : "10:00"
                    }>
                      {Array.from({ length: 96 }, (_, i) => {
                        const m = i * 15;
                        return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
                      }).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>Personel</label>
                    <select
                      name="employeeId"
                      required
                      value={isEmployee ? (selfId || "") : undefined}
                      defaultValue={isEmployee ? undefined : ""}
                      onChange={() => { }}
                      disabled={isEmployee}
                    >
                      {!isEmployee && <option value="">Seçin…</option>}
                      {employees.map(e => (
                        <option key={e.id} value={e.id}>{e.fullName || e.name || e.email}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label>Müşteri</label>
                    <select
                      value={selectedCustomerId}
                      onChange={(e) => setSelectedCustomerId(e.target.value)}
                      required
                    >
                      <option value="">Seçin…</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>
                          {(c.code ? `${c.code} – ` : "") + (c.name || c.fullName || c.title || c.email || `Müşteri #${c.id}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="field full">
                  <label>Mağaza</label>
                  <input
                    type="text"
                    placeholder="Mağaza ara…"
                    value={storeQ}
                    onChange={(e) => setStoreQ(e.target.value)}
                    disabled={!selectedCustomerId || storesLoading}
                  />
                  <div className="store-results">
                    {!selectedCustomerId && <div className="store-empty">Önce müşteri seçin</div>}
                    {selectedCustomerId && storesLoading && <div className="store-loading">Mağazalar yükleniyor…</div>}
                    {selectedCustomerId && !storesLoading && stores.length === 0 && storeQ.trim() && (
                      <div className="store-empty">Sonuç yok</div>
                    )}
                    {selectedCustomerId && !storesLoading && stores.map(s => (
                      <label key={s.id} className={`store-item ${selectedStoreId === s.id ? "on" : ""}`}>
                        <input
                          type="radio"
                          name="storeId-radio"
                          value={s.id}
                          checked={selectedStoreId === s.id}
                          onChange={() => setSelectedStoreId(s.id)}
                        />
                        <span>{s.code ? `${s.code} – ` : ""}{s.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="field full">
                  <label>Ziyaret Türü</label>
                  <select name="visitType" defaultValue="PERIYODIK" required>
                    <option value="PERIYODIK">{VISIT_TYPE_TR.PERIYODIK}</option>
                    <option value="ACIL_CAGRI">{VISIT_TYPE_TR.ACIL_CAGRI}</option>
                    <option value="ISTASYON_KURULUM">{VISIT_TYPE_TR.ISTASYON_KURULUM}</option>
                    <option value="ILK_ZIYARET">{VISIT_TYPE_TR.ILK_ZIYARET}</option>
                  </select>
                </div>

                <div className="field full">
                  <label>Notlar</label>
                  <textarea name="notes" rows={3} placeholder="(Opsiyonel) Not ekleyin…" />
                </div>

                <div className="field full recurrence-toggle">
                  <label>Ziyaret Türü</label>
                  <div className="type-switch">
                    <button 
                      type="button" 
                      className={!isRecurring ? "on" : ""} 
                      onClick={() => setIsRecurring(false)}
                    >Tek Seferlik</button>
                    <button 
                      type="button" 
                      className={isRecurring ? "on" : ""} 
                      onClick={() => setIsRecurring(true)}
                    >Tekrarlanan</button>
                  </div>
                </div>

                {isRecurring && (
                  <div className="recurrence-fields">
                    <div className="field">
                      <label>Sıklık</label>
                      <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                        <option value="WEEKLY">Haftalık</option>
                        <option value="BIWEEKLY">2 Haftada Bir</option>
                        <option value="TRIWEEKLY">3 Haftada Bir</option>
                        <option value="MONTHLY">Aylık</option>
                        <option value="QUARTERLY">Çeyreklik (3 Ay)</option>
                        <option value="YEARLY">Yıllık</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>Planlama Süresi</label>
                      <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                        <option value="3M">3 Ay Boyunca</option>
                        <option value="6M">6 Ay Boyunca</option>
                        <option value="1Y">1 Yıl Boyunca</option>
                        <option value="2Y">2 Yıl Boyunca</option>
                        <option value="3Y">3 Yıl Boyunca</option>
                        <option value="INFINITE">Sınırsız (10 Yıl)</option>
                      </select>
                    </div>
                  </div>
                )}
              </form>

              <div className="vc-modal-foot">
                <button className="btn ghost" onClick={() => setModalOpen(false)}>Vazgeç</button>
                <button className="btn" onClick={onCreate} disabled={saving}>
                  {saving ? "Kaydediliyor…" : "Planla"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE CONFIRM MODAL */}
        {deleteConfirm.open && (
          <div
            className="vc-modal-backdrop"
            onClick={(e) => {
              if (e.target.classList.contains("vc-modal-backdrop")) closeDeleteConfirm();
            }}
          >
            <div className="vc-modal vc-delete-modal">
              <div className="vc-modal-head" style={{ background: "#dc2626" }}>
                <div>Ziyaret Sil</div>
                <button className="close" onClick={closeDeleteConfirm}>×</button>
              </div>
              <div className="vc-delete-body">
                <p>Bu ziyareti silmek istediğinize emin misiniz?</p>
                <p className="delete-title">"{deleteConfirm.title}"</p>
                <p className="delete-warn">Bu işlem geri alınamaz.</p>
              </div>
              <div className="vc-modal-foot">
                <button className="btn ghost" onClick={closeDeleteConfirm} disabled={deleting}>Vazgeç</button>
                <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={deleting}>
                  {deleting ? "Siliniyor…" : "Evet, Sil"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
